import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import './CaptureSession.css';

// ── Prompt categories ────────────────────────────────────────────────────────
const PROMPT_CATEGORIES = [
  {
    id: 'life-story',
    title: 'Life Story',
    icon: '📖',
    description: 'Childhood, milestones, and defining moments',
    synthIntro: "Let's walk through your life story together. I'll ask you about the moments that shaped who you are — take your time with each one.",
    prompts: [
      "What's your earliest childhood memory? Paint the picture for me.",
      "Where did you grow up, and what was it like there?",
      "Who was your best friend growing up? What made that friendship special?",
      "What was your favorite thing to do as a kid?",
      "What was school like for you — were you into it, or counting the minutes?",
      "What's a moment from your younger years that really shaped who you are?",
      "Tell me about your first job. What did it teach you?",
      "Was there a turning point in your life where everything changed direction?",
      "If you could relive one day from your past, which would it be and why?",
      "What are you most proud of in your life so far?",
    ],
  },
  {
    id: 'relationships',
    title: 'Relationships',
    icon: '💛',
    description: 'Family, friends, and meaningful connections',
    synthIntro: "The people in our lives leave the deepest marks on our soul. Let's talk about the relationships that matter most to you.",
    prompts: [
      "Tell me about someone who changed your life in a meaningful way.",
      "What's your favorite memory with your family?",
      "Who do you admire most, and what is it about them?",
      "What's the best piece of advice someone ever gave you?",
      "Tell me about a friendship that has truly stood the test of time.",
      "How did you meet the most important person in your life?",
      "What would you want to say to a loved one right now if they were here?",
      "Who taught you the most about love — and what did you learn?",
      "What family tradition means the most to you?",
      "Describe a moment when someone made you feel truly seen and understood.",
    ],
  },
  {
    id: 'wisdom',
    title: 'Wisdom & Values',
    icon: '🌿',
    description: 'Lessons learned, beliefs, and perspectives',
    synthIntro: "Your wisdom comes from everything you've been through. Let's capture the beliefs and lessons that define your perspective.",
    prompts: [
      "What's the most important lesson life has taught you so far?",
      "What do you believe matters most in this life?",
      "If you could sit down with your younger self, what would you say?",
      "What's a mistake that ended up teaching you something invaluable?",
      "How do you define success — has that definition changed over time?",
      "What keeps you going during the hard times?",
      "What do you want future generations to know about who you were?",
      "Is there a belief you hold that most people wouldn't agree with?",
      "How has your perspective on life changed from where you started?",
      "What would you want your legacy to be?",
    ],
  },
  {
    id: 'identity',
    title: 'Identity & Self',
    icon: '🪞',
    description: 'Who you are, your personality, and what makes you unique',
    synthIntro: "Now let's get to the core of who you are — your identity, your quirks, the things that make you uniquely you.",
    prompts: [
      "If you had to describe yourself in three words, what would they be?",
      "What roles in life define you the most — parent, creator, friend, leader?",
      "What's a quirk about you that people who know you well would mention immediately?",
      "Are you more of an introvert or extrovert? Has that changed over time?",
      "What's your sense of humor like?",
      "What are you passionate about right now?",
      "How do you handle conflict or disagreements?",
      "What's something about you that most people don't know?",
      "What's the biggest way you've changed as a person in the last few years?",
      "If someone asked 'who are you?' and you couldn't mention your job, what would you say?",
    ],
  },
  {
    id: 'emotional',
    title: 'Emotional World',
    icon: '🌊',
    description: 'Feelings, fears, joys, and how you navigate emotions',
    synthIntro: "Your emotional landscape is a huge part of who you are. Let's explore how you feel, what moves you, and what you carry.",
    prompts: [
      "What emotion do you feel most often on a typical day?",
      "What's something that always makes you feel better when you're down?",
      "Is there a type of situation that consistently gets under your skin?",
      "How do you usually handle sadness — do you talk about it, retreat, or push through?",
      "What was the last thing that made you genuinely laugh out loud?",
      "What are you most afraid of, deep down?",
      "When was the last time you cried, and what was it about?",
      "What makes you feel most alive?",
      "How do you show love to the people you care about?",
      "What's a memory that still gives you chills — good or bad?",
    ],
  },
  {
    id: 'favorites',
    title: 'Favorites & Tastes',
    icon: '⭐',
    description: 'Foods, places, music, movies, and the things you love',
    synthIntro: "The things we love say a lot about who we are. Let's catalog the stuff that lights you up.",
    prompts: [
      "What's your all-time favorite meal or dish?",
      "What's a place that feels like home to you?",
      "What song or album means the most to you, and why?",
      "What's the best book you've ever read?",
      "What's your favorite way to spend a free day?",
      "What movie or show deeply moved you?",
      "What's a smell that instantly brings back a memory?",
      "If you could travel anywhere tomorrow, where would you go?",
      "What's your comfort food — the one you reach for when you need it?",
      "What's a hobby or activity that makes you lose track of time?",
    ],
  },
];

// ── Mini 3D Core Sphere for the soul dashboard ──────────────────────────────
function MiniCoreSphere({ overallScore }) {
  const meshRef = useRef();
  const glowRef = useRef();
  const particleCount = 1200;

  const positions = useMemo(() => {
    const pos = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = 0.85 + (Math.random() - 0.5) * 0.3;
      pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      pos[i * 3 + 2] = r * Math.cos(phi);
    }
    return pos;
  }, []);

  const colors = useMemo(() => {
    const col = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      const brightness = 0.5 + overallScore * 0.5;
      const variation = 0.7 + Math.random() * 0.3;
      col[i * 3] = 0.25 * brightness * variation;
      col[i * 3 + 1] = 0.55 * brightness * variation;
      col[i * 3 + 2] = 0.40 * brightness * variation;
    }
    return col;
  }, [overallScore]);

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.005;
      meshRef.current.rotation.x += 0.002;
      const pulse = 1.0 + Math.sin(state.clock.elapsedTime * 1.2) * 0.03;
      meshRef.current.scale.setScalar(pulse);
    }
    if (glowRef.current) {
      const glow = 0.3 + Math.sin(state.clock.elapsedTime * 0.8) * 0.1;
      glowRef.current.material.opacity = glow;
    }
  });

  return (
    <>
      <mesh ref={glowRef}>
        <sphereGeometry args={[0.6, 16, 16]} />
        <meshBasicMaterial color="#3b725f" transparent opacity={0.25} />
      </mesh>
      <points ref={meshRef}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={particleCount} array={positions} itemSize={3} />
          <bufferAttribute attach="attributes-color" count={particleCount} array={colors} itemSize={3} />
        </bufferGeometry>
        <pointsMaterial
          size={0.045}
          vertexColors
          transparent
          opacity={0.8 + overallScore * 0.2}
          sizeAttenuation
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </points>
    </>
  );
}

// ── Dimension key → label mapping ────────────────────────────────────────────
const DIM_LABELS = {
  identity: 'Identity',
  values_beliefs: 'Values & Beliefs',
  personality: 'Personality',
  emotional_landscape: 'Emotional Landscape',
  relationships: 'Relationships',
  life_story: 'Life Story',
  wisdom_perspective: 'Wisdom & Perspective',
  voice_expression: 'Voice & Expression',
};

const DIM_ICONS = {
  identity: '🪞',
  values_beliefs: '🌿',
  personality: '🎭',
  emotional_landscape: '🌊',
  relationships: '💛',
  life_story: '📖',
  wisdom_perspective: '🔮',
  voice_expression: '🗣️',
};

// ── Synth transition phrases (used between prompts) ──────────────────────────
const SYNTH_TRANSITIONS = [
  "That's really something. Thank you for sharing that. Here's what I'd love to know next —",
  "I can tell that means a lot to you. Let me ask you this —",
  "That's the kind of thing I want to make sure we preserve. Here's another one —",
  "Really insightful. Let's go deeper —",
  "I appreciate you opening up about that. Now —",
  "That paints a vivid picture. Let's explore this next —",
  "Noted. That's going to be important for your soul profile. Next —",
  "Beautiful. Let me ask you something a bit different —",
];

const GAP_TRANSITIONS = [
  (dim) => `Good, that helps fill in your ${dim} dimension. Let me dig into another gap —`,
  (dim) => `That's exactly the kind of thing I was missing from your ${dim} profile. Here's the next one —`,
  (dim) => `Perfect. Your ${dim} picture is getting clearer. Now let's work on this —`,
  (dim) => `That's going straight into your soul profile under ${dim}. Next gap —`,
];

// ── Speech Recognition helper ────────────────────────────────────────────────
const SpeechRecognition = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

function CaptureSession({ isOpen, onClose, onSendMessage, token, api }) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentPromptIdx, setCurrentPromptIdx] = useState(0);
  const [response, setResponse] = useState('');
  const [completedPrompts, setCompletedPrompts] = useState({});
  const [saving, setSaving] = useState(false);
  const [chatThread, setChatThread] = useState([]); // { role: 'synth'|'user', text: string }
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  // Soul awareness state
  const [soulProfile, setSoulProfile] = useState(null);
  const [discoveryPrompts, setDiscoveryPrompts] = useState([]);
  const [soulLoading, setSoulLoading] = useState(false);
  // For dynamic gap-filling mode
  const [gapPrompts, setGapPrompts] = useState([]); // [{dimension, label, score, prompt}, ...]
  const [gapPromptIdx, setGapPromptIdx] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const textareaRef = useRef(null);
  const chatEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);

  // ── TTS: Speak text aloud via backend ───────────────────────────────────
  const speakText = useCallback((text) => {
    if (!api || !token || !text) return;
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    // Strip dimension prefixes like "[Identity — 5%] " from the spoken text
    const cleanText = text.replace(/^\[.*?\]\s*/, '');
    setIsSpeaking(true);
    fetch(
      `${api}/api/memories/text-to-speech?text=${encodeURIComponent(cleanText)}&skip_transform=true`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
    )
      .then(res => res.ok ? res.json() : Promise.reject(res.status))
      .then(data => {
        if (data.audio_data) {
          const audio = new Audio(data.audio_data);
          audio.volume = 1.0;
          audioRef.current = audio;
          audio.onended = () => { setIsSpeaking(false); audioRef.current = null; };
          audio.onpause = () => setIsSpeaking(false);
          audio.play().catch(e => { console.warn('TTS play blocked:', e); setIsSpeaking(false); });
        } else {
          setIsSpeaking(false);
        }
      })
      .catch(err => { console.error('Capture TTS error:', err); setIsSpeaking(false); });
  }, [api, token]);

  // Fetch soul profile + discovery prompts when opened
  useEffect(() => {
    if (!isOpen || !token || !api) return;
    setSoulLoading(true);
    Promise.all([
      fetch(`${api}/soul_profile`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
      fetch(`${api}/soul_discovery?count=8`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
    ]).then(([profile, discovery]) => {
      if (profile) setSoulProfile(profile);
      if (discovery?.prompts) setDiscoveryPrompts(discovery.prompts);
    }).catch(e => console.error('Soul fetch error:', e))
      .finally(() => setSoulLoading(false));
  }, [isOpen, token, api]);

  // Auto-scroll chat thread to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatThread, interimTranscript]);

  // Focus textarea when prompt changes
  useEffect(() => {
    if (selectedCategory && textareaRef.current && !isListening) {
      textareaRef.current.focus();
    }
  }, [selectedCategory, currentPromptIdx, isListening]);

  // Cleanup speech recognition + audio on unmount or close
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Stop audio when closing
  useEffect(() => {
    if (!isOpen && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsSpeaking(false);
    }
  }, [isOpen]);

  // Derived soul data
  const overallScore = soulProfile?.overall_score || 0;
  const dimensions = soulProfile?.dimensions || {};
  const computeDepth = (count) => count > 0 ? Math.round(Math.log2(1 + count * 1.5) * 10) / 10 : 0;
  const getDimDepth = (dimKey) => {
    const d = dimensions[dimKey];
    return d?.depth ?? computeDepth(d?.evidence_count || 0);
  };
  const overallDepth = soulProfile?.overall_depth || Object.keys(DIM_LABELS).reduce((s, k) => s + getDimDepth(k), 0);
  const weakDims = Object.entries(dimensions)
    .sort(([,a], [,b]) => (a.score || 0) - (b.score || 0))
    .slice(0, 3)
    .map(([k, v]) => ({ key: k, label: DIM_LABELS[k] || k, depth: getDimDepth(k) }));

  // Start a category — add Synth intro + first prompt to chat thread
  const startCategory = useCallback((catId) => {
    const cat = PROMPT_CATEGORIES.find(c => c.id === catId);
    if (!cat) return;
    setSelectedCategory(catId);
    setCurrentPromptIdx(0);
    setGapPromptIdx(0);
    setResponse('');
    setChatThread([
      { role: 'synth', text: cat.synthIntro },
      { role: 'synth', text: cat.prompts[0], isPrompt: true },
    ]);
    // Speak the first prompt
    speakText(cat.prompts[0]);
  }, [speakText]);

  // Start the dynamic "Fill the Gaps" mode
  const startGapMode = useCallback(() => {
    if (discoveryPrompts.length === 0) return;
    setSelectedCategory('__gaps__');
    setGapPrompts(discoveryPrompts);
    setGapPromptIdx(0);
    setCurrentPromptIdx(0);
    setResponse('');

    const weakList = weakDims.map(d => `${d.label} (Depth ${d.depth})`).join(', ');
    const depth = Math.round(overallDepth);
    const intro = depth > 0
      ? `Your soul depth is ${depth} so far. I've identified some gaps — your shallowest areas are ${weakList}. Let me ask you some targeted questions to deepen those.`
      : `We haven't started mapping your soul yet. Let me ask you some foundational questions across the key dimensions to get us started.`;

    const first = discoveryPrompts[0];
    setChatThread([
      { role: 'synth', text: intro },
      { role: 'synth', text: `[${first.label} — Depth ${computeDepth(first.evidence_count || Math.round(first.score * 10))}] ${first.prompt}`, isPrompt: true },
    ]);
    // Speak the first gap prompt
    speakText(first.prompt);
  }, [discoveryPrompts, weakDims, overallScore, speakText]);

  const category = selectedCategory
    ? PROMPT_CATEGORIES.find(c => c.id === selectedCategory)
    : null;

  const completedInCategory = (catId) => {
    return completedPrompts[catId] ? Object.keys(completedPrompts[catId]).length : 0;
  };

  // ── Voice-to-text ────────────────────────────────────────────────────────
  const toggleListening = useCallback(() => {
    if (isListening) {
      // Stop
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
      return;
    }

    if (!SpeechRecognition) {
      alert('Voice input is not supported in this browser. Try Chrome or Edge.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }
      if (final) {
        setResponse(prev => prev + (prev ? ' ' : '') + final);
        setInterimTranscript('');
      } else {
        setInterimTranscript(interim);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      setInterimTranscript('');
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript('');
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening]);

  // ── Save response ────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!response.trim()) return;
    const isGapMode = selectedCategory === '__gaps__';
    if (!isGapMode && !category) return;

    // Stop listening if active
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      setInterimTranscript('');
    }

    setSaving(true);
    const userText = response.trim();

    if (isGapMode) {
      // ── Gap-filling mode ──────────────────────────────────────────────
      const currentGap = gapPrompts[gapPromptIdx];
      const message = `[Soul Discovery: ${currentGap?.label || 'Gap'}]\nQ: ${currentGap?.prompt || ''}\nA: ${userText}`;

      setChatThread(prev => [...prev, { role: 'user', text: userText }]);

      try {
        if (onSendMessage) await onSendMessage(message);
        setResponse('');

        const nextIdx = gapPromptIdx + 1;
        if (nextIdx < gapPrompts.length) {
          const nextGap = gapPrompts[nextIdx];
          const transitionFn = GAP_TRANSITIONS[Math.floor(Math.random() * GAP_TRANSITIONS.length)];
          const transition = transitionFn(currentGap?.label || 'that');
          setTimeout(() => {
            setChatThread(prev => [
              ...prev,
              { role: 'synth', text: transition },
              { role: 'synth', text: `[${nextGap.label} — Depth ${computeDepth(nextGap.evidence_count || Math.round(nextGap.score * 10))}] ${nextGap.prompt}`, isPrompt: true },
            ]);
            setGapPromptIdx(nextIdx);
            speakText(nextGap.prompt);
          }, 600);
        } else {
          setTimeout(() => {
            setChatThread(prev => [
              ...prev,
              {
                role: 'synth',
                text: `That's all ${gapPrompts.length} gap-filling questions answered. Your soul profile will deepen when it next ages. Great work.`,
                isComplete: true,
              },
            ]);
          }, 600);
        }
      } catch (e) {
        console.error('Failed to save gap capture:', e);
      }
    } else {
      // ── Normal category mode ──────────────────────────────────────────
      const prompt = category.prompts[currentPromptIdx];
      const message = `[Memory Capture: ${category.title}]\nQ: ${prompt}\nA: ${userText}`;

      setChatThread(prev => [...prev, { role: 'user', text: userText }]);

      try {
        if (onSendMessage) await onSendMessage(message);

        setCompletedPrompts(prev => ({
          ...prev,
          [selectedCategory]: {
            ...(prev[selectedCategory] || {}),
            [currentPromptIdx]: true,
          }
        }));

        setResponse('');

        const answered = { ...(completedPrompts[selectedCategory] || {}), [currentPromptIdx]: true };
        let next = currentPromptIdx + 1;
        while (next < category.prompts.length && answered[next]) next++;

        if (next < category.prompts.length) {
          const transition = SYNTH_TRANSITIONS[Math.floor(Math.random() * SYNTH_TRANSITIONS.length)];
          setTimeout(() => {
            setChatThread(prev => [
              ...prev,
              { role: 'synth', text: transition },
              { role: 'synth', text: category.prompts[next], isPrompt: true },
            ]);
            setCurrentPromptIdx(next);
            speakText(category.prompts[next]);
          }, 600);
        } else {
          const totalDone = Object.keys(answered).length + 1;
          setTimeout(() => {
            setChatThread(prev => [
              ...prev,
              {
                role: 'synth',
                text: `That's all ${totalDone} prompts in ${category.title} answered. These memories are now being preserved and analyzed. You're building something meaningful here.`,
                isComplete: true,
              },
            ]);
          }, 600);
        }
      } catch (e) {
        console.error('Failed to save capture:', e);
      }
    }
    setSaving(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSave();
    }
    // Also allow plain Enter (without shift) for quick send
    if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      handleSave();
    }
  };

  if (!isOpen) return null;

  // ── Category selection view ──────────────────────────────────────────────
  if (!category && selectedCategory !== '__gaps__') {
    const depth = Math.round(overallDepth);

    return (
      <div className="cap-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="cap-panel">
          <div className="cap-header">
            <div className="cap-synth-badge">
              <img src="/philosophical-icon.png" alt="Story" className="cap-synth-avatar" />
              <span>Story</span>
            </div>
            <h2 className="cap-title">Memory Capture</h2>
            <p className="cap-desc">I'm Story — your memory archiver. I'll guide you through questions designed to capture the essence of who you are. Pick a topic and let's begin.</p>
            <button className="cap-close" onClick={onClose}>&times;</button>
          </div>

          {/* Soul depth dashboard */}
          {!soulLoading && soulProfile && (
            <div className="cap-soul-dashboard">
              <div className="cap-soul-header-row">
                <div className="cap-soul-orb-mini">
                  <Canvas
                    camera={{ position: [0, 0, 3.5], fov: 50 }}
                    gl={{ antialias: true, alpha: true }}
                    dpr={[1, 1.5]}
                    style={{ background: 'transparent' }}
                  >
                    <ambientLight intensity={0.15} />
                    <pointLight position={[0, 0, 0]} intensity={0.5 + overallScore * 1.5} color="#3b725f" />
                    <MiniCoreSphere overallScore={overallScore} />
                  </Canvas>
                  <div className="cap-soul-orb-depth">
                    <span className="cap-soul-depth-value">{depth}</span>
                    <span className="cap-soul-depth-label">Depth</span>
                  </div>
                </div>
                <div className="cap-soul-header-text">
                  <div className="cap-soul-label">Soul Depth</div>
                  <div className="cap-soul-sub">
                    {depth === 0
                      ? 'No soul data yet — start capturing to build your profile'
                      : depth < 10
                        ? 'Early stages — keep sharing to deepen your profile'
                        : depth < 25
                          ? 'Taking shape — some dimensions are still shallow'
                          : 'Strong foundation — keep going, there\'s always more depth to discover'}
                  </div>
                </div>
              </div>
              <div className="cap-soul-dims">
                {Object.keys(DIM_LABELS).map(key => {
                  const dimData = dimensions[key] || {};
                  const dimDepth = getDimDepth(key);
                  return (
                    <div key={key} className="cap-soul-dim-row">
                      <span className="cap-soul-dim-icon">{DIM_ICONS[key]}</span>
                      <span className="cap-soul-dim-name">{DIM_LABELS[key]}</span>
                      <span className="cap-soul-dim-pct">{dimDepth}</span>
                    </div>
                  );
                })}
              </div>

              {/* Fill the Gaps CTA */}
              {discoveryPrompts.length > 0 && (
                <button className="cap-gap-btn" onClick={startGapMode}>
                  <span className="cap-gap-btn-icon">🎯</span>
                  <div className="cap-gap-btn-text">
                    <span className="cap-gap-btn-title">Fill the Gaps</span>
                    <span className="cap-gap-btn-sub">{discoveryPrompts.length} targeted questions for your weakest dimensions</span>
                  </div>
                </button>
              )}
            </div>
          )}

          {soulLoading && (
            <div className="cap-soul-loading">Analyzing your soul profile...</div>
          )}

          {/* Category separator */}
          <div className="cap-section-divider">
            <div className="cap-section-line" />
            <span className="cap-section-label">Or choose a topic</span>
            <div className="cap-section-line" />
          </div>

          <div className="cap-categories">
            {PROMPT_CATEGORIES.map(cat => {
              const done = completedInCategory(cat.id);
              const total = cat.prompts.length;
              return (
                <div
                  key={cat.id}
                  className="cap-cat-card"
                  onClick={() => startCategory(cat.id)}
                >
                  <div className="cap-cat-icon">{cat.icon}</div>
                  <div className="cap-cat-info">
                    <div className="cap-cat-title">{cat.title}</div>
                    <div className="cap-cat-subtitle">{cat.description}</div>
                    {done > 0 && (
                      <div className="cap-cat-progress">
                        <div className="cap-cat-bar">
                          <div className="cap-cat-bar-fill" style={{ width: `${(done / total) * 100}%` }} />
                        </div>
                        <span className="cap-cat-count">{done}/{total}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── Conversational capture view ──────────────────────────────────────────
  const isGapMode = selectedCategory === '__gaps__';
  const answered = isGapMode ? {} : (completedPrompts[selectedCategory] || {});
  const answeredCount = isGapMode ? gapPromptIdx : Object.keys(answered).length;
  const totalPrompts = isGapMode ? gapPrompts.length : (category?.prompts?.length || 0);
  const isCurrentAnswered = isGapMode ? false : answered[currentPromptIdx];

  return (
    <div className="cap-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cap-panel cap-chat-panel">
        {/* Header */}
        <div className="cap-chat-header">
          <button className="cap-back" onClick={() => { setSelectedCategory(null); setResponse(''); setChatThread([]); setGapPrompts([]); }}>
            ←
          </button>
          <div className="cap-chat-header-info">
            <img src="/philosophical-icon.png" alt="Story" className="cap-synth-avatar-sm" />
            <div>
              <div className="cap-chat-header-name">Story {isSpeaking && <span className="cap-speaking-indicator"><span /><span /><span /></span>}</div>
              <div className="cap-chat-header-cat">
                {isGapMode
                  ? `🎯 Fill the Gaps — ${answeredCount}/${totalPrompts}`
                  : `${category?.icon || ''} ${category?.title || ''} — ${answeredCount}/${totalPrompts}`
                }
              </div>
            </div>
          </div>
          <button className="cap-close" onClick={onClose}>&times;</button>
        </div>

        {/* Prompt navigation dots */}
        <div className="cap-prompt-dots">
          {Array.from({ length: totalPrompts }).map((_, i) => (
            <div
              key={i}
              className={`cap-pdot ${i === (isGapMode ? gapPromptIdx : currentPromptIdx) ? 'cap-pdot-active' : ''} ${isGapMode ? (i < gapPromptIdx ? 'cap-pdot-done' : '') : (answered[i] ? 'cap-pdot-done' : '')}`}
              title={`Prompt ${i + 1}`}
            />
          ))}
        </div>

        {/* Chat thread */}
        <div className="cap-chat-thread">
          {chatThread.map((msg, i) => (
            <div
              key={i}
              className={`cap-msg ${msg.role === 'synth' ? 'cap-msg-synth' : 'cap-msg-user'} ${msg.isPrompt ? 'cap-msg-prompt' : ''} ${msg.isComplete ? 'cap-msg-complete' : ''}`}
            >
              {msg.role === 'synth' && (
                <div className="cap-msg-avatar">
                  <img src="/philosophical-icon.png" alt="S" className="cap-msg-avatar-img" />
                </div>
              )}
              <div className={`cap-msg-bubble ${msg.role === 'synth' ? 'cap-msg-bubble-synth' : 'cap-msg-bubble-user'}`}>
                {msg.text}
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>

        {/* Input area */}
        {!isCurrentAnswered && (
          <div className="cap-input-area">
            <div className="cap-input-row">
              {/* Mic button */}
              <button
                className={`cap-mic-btn ${isListening ? 'cap-mic-active' : ''}`}
                onClick={toggleListening}
                title={isListening ? 'Stop listening' : 'Speak your response'}
                type="button"
              >
                {isListening ? (
                  <span className="cap-mic-recording">
                    <span className="cap-mic-pulse" />
                    ■
                  </span>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" y1="19" x2="12" y2="23"/>
                    <line x1="8" y1="23" x2="16" y2="23"/>
                  </svg>
                )}
              </button>

              {/* Text input */}
              <textarea
                ref={textareaRef}
                className="cap-input"
                placeholder={isListening ? 'Listening... speak now' : 'Type or tap the mic to speak...'}
                value={response + (interimTranscript ? (response ? ' ' : '') + interimTranscript : '')}
                onChange={(e) => { setResponse(e.target.value); setInterimTranscript(''); }}
                onKeyDown={handleKeyDown}
                rows={1}
                style={{ height: Math.min(120, Math.max(42, (response.split('\n').length) * 22)) }}
              />

              {/* Send button */}
              <button
                className="cap-send-btn"
                onClick={handleSave}
                disabled={!(response.trim() || interimTranscript.trim()) || saving}
                title="Send (Enter)"
              >
                {saving ? (
                  <span className="cap-send-saving">...</span>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                  </svg>
                )}
              </button>
            </div>
            {isListening && (
              <div className="cap-listening-indicator">
                <span className="cap-listening-dot" />
                <span className="cap-listening-dot" />
                <span className="cap-listening-dot" />
                <span>Listening — speak naturally</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CaptureSession;
